import {} from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import {
  isPlatform,
  useIonToast,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IntercomArticle,
  useLaunchIntercomArticle,
} from '../../config/intercom';

interface RegisterPushFunctionOptions {
  skipRegisterIfAlreadyGranted?: boolean;
  hideDeniedPrompt?: boolean;
  hideRequestPrompt?: boolean;
}

type RegisterPushFunction = (
  options?: RegisterPushFunctionOptions
) => Promise<boolean>;

const useRegisterPushNotification = (): [RegisterPushFunction] => {
  const { i18n } = useTranslation();
  const registerPushNotification = useCallback(
    async (options?: RegisterPushFunctionOptions): Promise<boolean> => {
      const mergedOpts: RegisterPushFunctionOptions = {
        skipRegisterIfAlreadyGranted: false,
        hideDeniedPrompt: false,
        hideRequestPrompt: false,
        ...options,
      };

      if (!isPlatform('capacitor')) {
        return true;
      }

      const existingPermission = await PushNotifications.checkPermissions();

      if (existingPermission.receive === 'granted') {
        if (!mergedOpts.skipRegisterIfAlreadyGranted) {
          await PushNotifications.register();
        }
        return true;
      } else if (
        existingPermission.receive === 'denied' &&
        !mergedOpts.hideDeniedPrompt
      ) {
        await Dialog.alert({
          title: i18n.t('common:event_request_permission'),
          message: i18n.t('common:event_push_notification_update_settings'),
        });
        return false;
      } else if (
        isPlatform('ios') &&
        existingPermission.receive === 'prompt' &&
        !mergedOpts.hideRequestPrompt
      ) {
        const result = await Dialog.confirm({
          title: i18n.t('common:event_request_permission'),
          message: i18n.t('common:push_notification_permission'),
          okButtonTitle: i18n.t('common:ok'),
          cancelButtonTitle: i18n.t('common:cancel'),
        });

        if (result.value) {
          const permission = await PushNotifications.requestPermissions();

          if (permission.receive === 'granted') {
            await PushNotifications.register();
            return true;
          }
        }
      }

      return false;
    },
    [i18n]
  );

  return [registerPushNotification];
};

const useUserPushNotificationDeniedToast = () => {
  const [present, dismiss] = usePushNotificationsUnavailableToastLazy();
  const pushNotificationsDenied = useCheckPushNotificationsDenied();

  const display = useCallback(async () => {
    const isDenied = await pushNotificationsDenied();
    if (isDenied) {
      present();
    }
  }, [present, pushNotificationsDenied]);

  useIonViewDidEnter(() => {
    display();
  });

  useIonViewWillLeave(() => {
    dismiss();
  });
};

const useCheckPushNotificationsDenied = (): (() => Promise<boolean>) => {
  const checkPushNotificationsDenied = useCallback(async () => {
    if (isPlatform('capacitor')) {
      let permStatus: string | undefined;
      if (isPlatform('android')) {
        // for android we are required to use the local-notifications plugin as documented here https://capacitorjs.com/docs/apis/push-notifications#checkpermissions
        // since android can always RECEIVE, but the user may choose to not DISPLAY. See API details https://capacitorjs.com/docs/apis/local-notifications#checkpermissions
        const result = await LocalNotifications.checkPermissions();
        permStatus = result.display;
      } else {
        const result = await PushNotifications.checkPermissions();
        permStatus = result.receive;
      }

      if (permStatus === 'denied') {
        return true;
      }
    }
    return false;
  }, []);

  return checkPushNotificationsDenied;
};

type PushNotificationsUnavailableToastLazyReturn = [() => void, () => void];

const usePushNotificationsUnavailableToastLazy =
  (): PushNotificationsUnavailableToastLazyReturn => {
    const { i18n } = useTranslation();
    const [present, dismiss] = useIonToast();
    const launchArticle = useLaunchIntercomArticle();

    const display = (message?: string) => {
      present({
        message: message ?? i18n.t('common:push_notifications_were_denied'),
        color: 'danger',
        duration: 5000,
        buttons: [
          {
            text: i18n.t('common:learn_more'),
            handler: () => {
              launchArticle(IntercomArticle.ENABLING_PUSH_NOTIFICATIONS);
            },
          },
        ],
      });
    };
    return [display, dismiss];
  };

export {
  useRegisterPushNotification,
  useUserPushNotificationDeniedToast,
  useCheckPushNotificationsDenied,
  usePushNotificationsUnavailableToastLazy,
};
